import { ErrorMessage, Field, Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { useShallow } from "zustand/react/shallow";
import Breadcrumb from "../../components/Breadcrumb";
import Input from "../../components/form/Input";
import ConfirmDialog from "../../components/modal/ConfirmDialog";
import Modal, { ModalBody, ModalFooter, ModalHeader } from "../../components/modal/Modal";
import Spinner from "../../components/misc/Spinner";
import { DEV_MODE } from "../../constants";
import NotificationsTab from "../settings/NotificationsTab";
import { Role, useAuthStore } from "../../stores/auth";
import { useToastStore } from "../../stores/toast";
import { useUserStore } from "../../stores/users";
import { formatDateTime } from "../../utils/dateFormat";

const createInitialValues = (user) => ({
  user: {
    id: user?.id || "",
    name: user?.name || "",
    username: user?.username || "",
    email: user?.email || "",
    number: user?.number || "",
    active: user?.active ?? true,
    role_ids: user?.roles?.map((r) => r.id) || [],
  },
});

export const composeRoleIds = ({
  roles,
  backofficeRoleName = null,
  technician = false,
}) => {
  const backofficeRole = roles.find((role) => role.name === backofficeRoleName);
  const technicianRole = roles.find((role) => role.name === Role.TECHNICIAN);
  const technicianEnabled =
    technician && backofficeRoleName !== Role.ADMINISTRATOR && technicianRole;

  return [
    ...(backofficeRole ? [backofficeRole.id] : []),
    ...(technicianEnabled ? [technicianRole.id] : []),
  ];
};

const createValidationSchema = (t) =>
  Yup.object().shape({
    user: Yup.object().shape({
      name: Yup.string().required(t("form.errors.required", { attr: t("users.name") })),
      username: Yup.string().required(t("form.errors.required", { attr: t("users.username") })),
      email: Yup.string()
        .email(t("form.errors.email"))
        .required(t("form.errors.required", { attr: t("users.email") })),
      role_ids: Yup.array()
        .min(1, t("form.errors.required", { attr: t("users.roles") }))
        .required(t("form.errors.required", { attr: t("users.roles") })),
    }),
  });

function UserAddEdit() {
  const { t } = useTranslation();
  const params = useParams();
  const userId = Number.parseInt(params.userId);
  const isAddMode = !userId;
  const navigate = useNavigate();
  const addToast = useToastStore((state) => state.addToast);

  const hasRole = useAuthStore((state) => state.hasRole);
  const isAdmin = hasRole(Role.ADMINISTRATOR);
  const isManager = hasRole(Role.MANAGER);
  const canView = isAdmin || isManager;

  const {
    byIds,
    roles,
    getById,
    fetchRoles,
    createOne,
    updateOne,
    sendResetPassword,
    resetPassword,
  } = useUserStore(
    useShallow((state) => ({
      byIds: state.byIds,
      roles: state.roles,
      getById: state.getById,
      fetchRoles: state.fetchRoles,
      createOne: state.createOne,
      updateOne: state.updateOne,
      sendResetPassword: state.sendResetPassword,
      resetPassword: state.resetPassword,
    })),
  );

  const user = Number.isInteger(userId) ? byIds[userId] : null;
  const targetIsAdmin = user?.roles?.some((role) => role.name === Role.ADMINISTRATOR);
  const canEdit = isAdmin || (isManager && !targetIsAdmin);

  const [isSendingReset, setIsSendingReset] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState(null);
  const [activeTab, setActiveTab] = useState("user");

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userId) {
      getById({ id: userId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleConfirmResetPassword = async () => {
    setShowResetConfirm(false);
    setIsResettingPassword(true);
    const { data, error } = await resetPassword({ id: userId });
    setIsResettingPassword(false);
    if (error) {
      const msg = error?.detail?.[0]?.t || error?.detail;
      addToast("error", msg ? t(msg) : t("users.reset_password_error"));
      return;
    }
    setGeneratedPassword(data?.password ?? null);
  };

  const handleCopyPassword = async () => {
    if (!generatedPassword) return;
    try {
      await navigator.clipboard.writeText(generatedPassword);
      addToast("success", t("users.password_copied"));
    } catch {
      addToast("error", t("users.password_copy_failed"));
    }
  };

  const handleSubmit = async (values) => {
    const payload = {
      name: values.user.name,
      username: values.user.username,
      email: values.user.email,
      number: values.user.number || null,
      active: values.user.active,
      role_ids: values.user.role_ids,
    };
    const { error } = isAddMode
      ? await createOne(payload)
      : await updateOne({ id: userId, ...payload });

    if (error) {
      const msg = error?.detail?.[0]?.t || error?.detail;
      addToast("error", msg ? t(msg) : JSON.stringify(error));
    } else {
      addToast("success", t("common.saved"));
    }
  };

  if (Number.isInteger(userId) && !user) {
    return <Spinner />;
  }

  if (!canView) {
    navigate("/");
  }

  const canSubmit = canEdit;

  const showNotificationsTab = !isAddMode;
  const technicianRole = roles.find((role) => role.name === Role.TECHNICIAN);
  const backofficeRoles = roles.filter(
    (role) =>
      [Role.MANAGER, Role.OFFICE, Role.ADMINISTRATOR].includes(role.name) &&
      (isAdmin || role.name !== Role.ADMINISTRATOR || targetIsAdmin),
  );

  return (
    <div>
      <Breadcrumb
        items={[{ label: t("users.users"), to: "/users" }, { label: isAddMode ? t("menu.add") : user.name }]}
      />

      {!canSubmit && <div className="alert alert-danger">{t("permissions.can_not_edit")}</div>}

      {showNotificationsTab && (
        <ul className="nav nav-tabs nav-tabs-v2 mb-3">
          <li className="nav-item me-2">
            <button
              type="button"
              className={`nav-link px-2 ${activeTab === "user" ? "active" : ""}`}
              onClick={() => setActiveTab("user")}
            >
              {t("users.user")}
            </button>
          </li>
          <li className="nav-item me-2">
            <button
              type="button"
              className={`nav-link px-2 ${activeTab === "notifications" ? "active" : ""}`}
              onClick={() => setActiveTab("notifications")}
            >
              {t("notifications.tab.title")}
            </button>
          </li>
        </ul>
      )}

      {showNotificationsTab && activeTab === "notifications" && (
        <NotificationsTab userId={userId} />
      )}

      <div style={{ display: activeTab === "user" ? undefined : "none" }}>
      <Formik
        initialValues={createInitialValues(user)}
        validationSchema={createValidationSchema(t)}
        validateOnChange={true}
        validateOnBlur={false}
        enableReinitialize={false}
        validateOnMount={false}
        onSubmit={handleSubmit}
      >
        {({ values, errors, isSubmitting, setFieldValue }) => (
          <Form>
            <div className="row">
              <div className="col">
                <div className="card border-0 mb-4">
                  <div className="card-header h6 mb-0 bg-none p-3">
                    <i className="fa fa-user fa-lg fa-fw text-dark text-opacity-50 me-1"></i> {t("users.user")}
                  </div>
                  <div className="card-body">
                    <div className="row mb-3 gx-3">
                      <div className="col-md-9 mb-3 mb-md-0">
                        <label className="form-label" htmlFor="user.name">
                          {t("users.name")}
                        </label>
                        <Field
                          component={Input}
                          name="user.name"
                          className="form-control fs-13px h-45px"
                          disabled={!canEdit}
                        />
                        <ErrorMessage
                          name="user.name"
                          render={(msg) => <div className="invalid-feedback">{msg}</div>}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label" htmlFor="user.number">
                          {t("users.number")}
                        </label>
                        <Field
                          component={Input}
                          name="user.number"
                          className="form-control fs-13px h-45px"
                          disabled={!canEdit}
                        />
                      </div>
                    </div>

                    <div className="row mb-3 gx-3">
                      <div className="col-md-6 mb-3 mb-md-0">
                        <label className="form-label" htmlFor="user.username">
                          {t("users.username")}
                        </label>
                        <Field
                          component={Input}
                          name="user.username"
                          className="form-control fs-13px h-45px"
                          disabled={!canEdit}
                        />
                        <ErrorMessage
                          name="user.username"
                          render={(msg) => <div className="invalid-feedback">{msg}</div>}
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label" htmlFor="user.email">
                          {t("users.email")}
                        </label>
                        <Field
                          component={Input}
                          name="user.email"
                          type="email"
                          className="form-control fs-13px h-45px"
                          disabled={!canEdit}
                        />
                        <ErrorMessage
                          name="user.email"
                          render={(msg) => <div className="invalid-feedback">{msg}</div>}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card border-0 mb-4">
                  <div className="card-header h6 mb-0 bg-none p-3"></div>
                  <div className="card-body">
                    <div className="btn btn-group d-flex">
                      <button className="btn btn-success w-100" disabled={isSubmitting || !canSubmit}>
                        {t("menu.submit")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-lg-4">
                <div className="card border-0 mb-4">
                  <div className="card-header h6 mb-0 bg-none p-3">
                    <i className="fa fa-shield-halved fa-lg fa-fw text-dark text-opacity-50 me-1"></i>{" "}
                    {t("users.roles")}
                  </div>
                  <div className="card-body">
                    <fieldset disabled={!canEdit}>
                      <legend className="form-label fs-6">{t("users.backoffice_access")}</legend>
                      <div className="form-check mb-2">
                        <input
                          type="radio"
                          className="form-check-input"
                          name="user_backoffice_role"
                          id="role-backoffice-none"
                          checked={
                            !backofficeRoles.some((role) =>
                              values.user.role_ids.includes(role.id),
                            )
                          }
                          onChange={() =>
                            setFieldValue(
                              "user.role_ids",
                              composeRoleIds({
                                roles,
                                technician:
                                  technicianRole &&
                                  values.user.role_ids.includes(technicianRole.id),
                              }),
                            )
                          }
                        />
                        <label className="form-check-label" htmlFor="role-backoffice-none">
                          {t("users.no_backoffice_access")}
                        </label>
                      </div>
                      {backofficeRoles.map((role) => (
                        <div className="form-check mb-2" key={role.id}>
                          <input
                            type="radio"
                            className="form-check-input"
                            name="user_backoffice_role"
                            id={`role-${role.id}`}
                            checked={values.user.role_ids.includes(role.id)}
                            disabled={role.name === Role.ADMINISTRATOR && !isAdmin}
                            onChange={() =>
                              setFieldValue(
                                "user.role_ids",
                                composeRoleIds({
                                  roles,
                                  backofficeRoleName: role.name,
                                  technician:
                                    technicianRole &&
                                    values.user.role_ids.includes(technicianRole.id),
                                }),
                              )
                            }
                          />
                          <label className="form-check-label" htmlFor={`role-${role.id}`}>
                            {t(`users.role_names.${role.name}`, role.name)}
                          </label>
                        </div>
                      ))}
                      {technicianRole && (
                        <div className="form-check border-top pt-3 mt-3">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="role-technician"
                            checked={values.user.role_ids.includes(technicianRole.id)}
                            disabled={backofficeRoles.some(
                              (role) =>
                                role.name === Role.ADMINISTRATOR &&
                                values.user.role_ids.includes(role.id),
                            )}
                            onChange={(event) => {
                              const selectedBackofficeRole = backofficeRoles.find(
                                (role) => values.user.role_ids.includes(role.id),
                              );
                              setFieldValue(
                                "user.role_ids",
                                composeRoleIds({
                                  roles,
                                  backofficeRoleName: selectedBackofficeRole?.name,
                                  technician: event.target.checked,
                                }),
                              );
                            }}
                          />
                          <label className="form-check-label" htmlFor="role-technician">
                            {t("users.technician_access")}
                          </label>
                        </div>
                      )}
                      <ErrorMessage
                        name="user.role_ids"
                        render={(msg) => <div className="text-danger mt-2">{msg}</div>}
                      />
                    </fieldset>
                  </div>
                </div>

                <div className="card border-0 mb-4">
                  <div className="card-header h6 mb-0 bg-none p-3">
                    <i className="fa fa-toggle-on fa-lg fa-fw text-dark text-opacity-50 me-1"></i> {t("users.status")}
                  </div>
                  <div className="card-body">
                    <div className="form-check form-switch">
                      <Field
                        type="checkbox"
                        name="user.active"
                        className="form-check-input"
                        id="user.active"
                        disabled={!canEdit}
                      />
                      <label className="form-check-label" htmlFor="user.active">
                        {t("users.active")}
                      </label>
                    </div>
                  </div>
                </div>

                {!isAddMode && (
                  <div className="card border-0 mb-4">
                    <div className="card-header h6 mb-0 bg-none p-3">
                      <i className="fa fa-clock-rotate-left fa-lg fa-fw text-dark text-opacity-50 me-1" />
                      {t("users.login_info")}
                    </div>
                    <div className="card-body">
                      {user.login_count > 0 ? (
                        <dl className="row mb-0 fs-13px">
                          <dt className="col-5 fw-normal text-dark text-opacity-50">{t("users.login_count")}</dt>
                          <dd className="col-7 mb-2">{user.login_count}</dd>
                          <dt className="col-5 fw-normal text-dark text-opacity-50">{t("users.current_login_at")}</dt>
                          <dd className="col-7 mb-2">{formatDateTime(user.current_login_at)}</dd>
                          <dt className="col-5 fw-normal text-dark text-opacity-50">{t("users.current_login_ip")}</dt>
                          <dd className="col-7 mb-2">{user.current_login_ip || "—"}</dd>
                          <dt className="col-5 fw-normal text-dark text-opacity-50">{t("users.last_login_at")}</dt>
                          <dd className="col-7 mb-2">
                            {user.last_login_at ? formatDateTime(user.last_login_at) : "—"}
                          </dd>
                          <dt className="col-5 fw-normal text-dark text-opacity-50">{t("users.last_login_ip")}</dt>
                          <dd className="col-7 mb-2">{user.last_login_ip || "—"}</dd>
                          <dt className="col-5 fw-normal text-dark text-opacity-50">{t("users.last_seen")}</dt>
                          <dd className="col-7 mb-0">{user.last_seen ? formatDateTime(user.last_seen) : "—"}</dd>
                        </dl>
                      ) : (
                        <div className="text-dark text-opacity-50 fs-13px">{t("users.never_logged_in")}</div>
                      )}
                    </div>
                  </div>
                )}

                {!isAddMode && (
                  <div className="card border-0 mb-4">
                    <div className="card-header h6 mb-0 bg-none p-3">
                      <i className="fa fa-key fa-lg fa-fw text-dark text-opacity-50 me-1" /> {t("users.password")}
                    </div>
                    <div className="card-body d-grid gap-2">
                      <button
                        type="button"
                        className="btn btn-default"
                        disabled={!canEdit || isSendingReset || isResettingPassword}
                        title={!canEdit ? t("users.reset_password_admin_only") : ""}
                        onClick={async () => {
                          setIsSendingReset(true);
                          const { error } = await sendResetPassword({ id: userId });
                          setIsSendingReset(false);
                          if (error) {
                            addToast("error", t("users.reset_password_error"));
                          } else {
                            addToast("success", t("users.reset_password_sent"));
                          }
                        }}
                      >
                        <i className={`fa ${isSendingReset ? "fa-spinner fa-spin" : "fa-envelope"} me-1`} />
                        {t("users.send_reset_password")}
                      </button>
                      {isAdmin && (
                        <button
                          type="button"
                          className="btn btn-default"
                          disabled={isResettingPassword || isSendingReset}
                          onClick={() => setShowResetConfirm(true)}
                        >
                          <i
                            className={`fa ${
                              isResettingPassword ? "fa-spinner fa-spin" : "fa-key"
                            } me-1`}
                          />
                          {t("users.reset_password")}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {DEV_MODE && (
              <pre
                className="m-t-xl"
                style={{
                  fontSize: "1rem",
                  padding: ".25rem .5rem",
                  overflowX: "scroll",
                }}
              >
                VALUES: {JSON.stringify(values, null, 2)}
                <br />
                ERRORS: {JSON.stringify(errors, null, 2)}
              </pre>
            )}
          </Form>
        )}
      </Formik>
      </div>
      {showResetConfirm && (
        <ConfirmDialog
          title={t("users.reset_password")}
          message={t("users.reset_password_confirm")}
          confirmLabel={t("users.reset_password")}
          onCancel={() => setShowResetConfirm(false)}
          onConfirm={handleConfirmResetPassword}
        />
      )}
      {generatedPassword !== null && (
        <Modal>
          <ModalHeader>
            <h5 className="modal-title">{t("users.reset_password_generated")}</h5>
          </ModalHeader>
          <ModalBody>
            <p className="mb-3">{t("users.reset_password_warning")}</p>
            <div className="input-group">
              <input
                type="text"
                readOnly
                value={generatedPassword}
                className="form-control fs-14px font-monospace"
                onFocus={(e) => e.target.select()}
              />
              <button
                type="button"
                className="btn btn-default"
                onClick={handleCopyPassword}
                title={t("users.copy_password")}
              >
                <i className="fa fa-copy" />
              </button>
            </div>
          </ModalBody>
          <ModalFooter>
            <button
              type="button"
              className="btn btn-success"
              onClick={() => setGeneratedPassword(null)}
            >
              {t("common.close")}
            </button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
}

export default UserAddEdit;
